import {
	LegalLayout,
	type LegalSection,
} from '#app/components/marketing/legal-layout.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/privacy.ts'

export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'Privacy Policy - Syrcha Privacy Compliance Scanner',
		description:
			'Privacy Policy for Syrcha. Learn what data we collect during compliance scans, how we use it, your rights under GDPR and CCPA, and how we protect your information.',
		matches,
	})

export default function PrivacyPolicyRoute() {
	const sections: LegalSection[] = [
		{
			id: 'scope',
			title: 'Scope',
			body: (
				<>
					<p>
						This policy applies to all users of Syrcha's Services worldwide. It
						covers information collected:
					</p>
					<ul>
						<li>When you visit our website or platform.</li>
						<li>When you register for an account.</li>
						<li>When you submit websites or apps for compliance scanning.</li>
						<li>When you communicate with us (support, sales, inquiries).</li>
					</ul>
				</>
			),
		},
		{
			id: 'information-collect',
			title: 'Information We Collect',
			body: (
				<>
					<p>
						We collect the following categories of personal and service-related
						information:
					</p>
					<h3>a. Account Information</h3>
					<ul>
						<li>Name, email address, company name, and billing details.</li>
						<li>Login credentials (encrypted).</li>
					</ul>
					<h3>b. Service Data</h3>
					<ul>
						<li>URLs and application endpoints you provide for scanning.</li>
						<li>Scan results, compliance reports, and analysis logs.</li>
					</ul>
					<h3>c. Technical Data</h3>
					<ul>
						<li>
							IP addresses, device identifiers, browser type, and operating
							system.
						</li>
						<li>Log files and diagnostic data.</li>
						<li>Cookies and similar technologies (see Section 9).</li>
					</ul>
					<h3>d. Payment Information</h3>
					<ul>
						<li>
							Card details are entered with, and held by, Stripe. They never
							reach our servers and we cannot see them.
						</li>
						<li>
							We store what Stripe returns to us: the plan, the amount, the last
							four digits, and the invoice history.
						</li>
					</ul>
					<h3>e. Communication Data</h3>
					<ul>
						<li>
							Records of support requests, feedback, or interactions with our
							team.
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'how-we-use',
			title: 'How We Use Information',
			body: (
				<>
					<p>We process collected information for the following purposes:</p>
					<ul>
						<li>
							<strong>Service Delivery</strong>: To provide compliance scans,
							reports, and monitoring.
						</li>
						<li>
							<strong>Account Management</strong>: To register, authenticate,
							and manage user accounts.
						</li>
						<li>
							<strong>Payments</strong>: To process transactions and issue
							invoices.
						</li>
						<li>
							<strong>Service Improvement</strong>: To troubleshoot, analyze,
							and enhance our Services.
						</li>
						<li>
							<strong>Communications</strong>: To send service updates, security
							alerts, and product information.
						</li>
						<li>
							<strong>Legal Compliance</strong>: To comply with applicable laws,
							enforce agreements, and protect rights.
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'legal-bases',
			title: 'Legal Bases for Processing (GDPR)',
			body: (
				<>
					<p>
						If you are located in the European Economic Area (EEA), we rely on
						the following legal bases:
					</p>
					<ul>
						<li>
							<strong>Contractual necessity</strong> (providing Services you
							request).
						</li>
						<li>
							<strong>Legitimate interests</strong> (improving Services,
							ensuring security).
						</li>
						<li>
							<strong>Consent</strong> (marketing emails, non-essential
							cookies).
						</li>
						<li>
							<strong>Legal obligations</strong> (compliance with financial and
							regulatory requirements).
						</li>
					</ul>
				</>
			),
		},
		{
			id: 'sharing',
			title: 'Sharing and Disclosure',
			body: (
				<>
					<p>We do not sell personal data. We may share information with:</p>
					<p>
						These are every sub-processor we use, what each one does, and where
						it runs. If this list changes, this page changes with it.
					</p>
					<ul>
						<li>
							<strong>Hetzner</strong> (Germany) - hosting for the application,
							the database, and the scanner. All service data lives here.
						</li>
						<li>
							<strong>Stripe</strong>: payments, invoicing, and card handling.
						</li>
						<li>
							<strong>Resend</strong>: transactional email for sign-in links,
							scan notifications, receipts.
						</li>
						<li>
							<strong>PostHog</strong> (EU Cloud) - product analytics. Requests
							are proxied through our own domain, so your browser never contacts
							PostHog directly.
						</li>
						<li>
							<strong>Sentry</strong>: error monitoring. Receives stack traces
							and the request context attached to a fault.
						</li>
						<li>
							<strong>S3-compatible object storage</strong>: encrypted database
							backups.
						</li>
					</ul>
					<p>Beyond those, we disclose personal data only:</p>
					<ul>
						<li>
							<strong>To legal authorities</strong>: where required by law, or
							to protect rights and safety.
						</li>
						<li>
							<strong>In a business transfer</strong>: on a merger, acquisition,
							or sale of assets.
						</li>
					</ul>
					<p>
						We do not sell personal data, and we do not share it with
						advertising networks or data brokers.
					</p>
				</>
			),
		},
		{
			id: 'retention',
			title: 'Data Retention',
			body: (
				<>
					<ul>
						<li>
							<strong>Account Data</strong>: Retained as long as your account is
							active.
						</li>
						<li>
							<strong>Scan Data</strong>: Retained for 12 months unless deleted
							earlier by the user.
						</li>
						<li>
							<strong>Logs</strong>: Retained for 90 days for security and
							diagnostics.
						</li>
						<li>
							<strong>Legal Records</strong>: Retained as required by law (e.g.,
							tax obligations).
						</li>
					</ul>
					<p>
						Users may request deletion of their data at any time (see Section
						8).
					</p>
				</>
			),
		},
		{
			id: 'security',
			title: 'Security',
			body: (
				<>
					<p>
						We implement appropriate technical and organizational measures,
						including:
					</p>
					<ul>
						<li>Encryption in transit (TLS) and at rest.</li>
						<li>Access controls and authentication safeguards.</li>
						<li>Regular vulnerability assessments and monitoring.</li>
					</ul>
					<p>
						However, no method of transmission over the Internet is 100% secure.
					</p>
				</>
			),
		},
		{
			id: 'your-rights',
			title: 'Your Rights',
			body: (
				<>
					<p>Depending on your location, you may have the following rights:</p>
					<h3>Under GDPR (EU/EEA):</h3>
					<ul>
						<li>Access, rectify, or erase personal data.</li>
						<li>Restrict or object to processing.</li>
						<li>Data portability.</li>
						<li>Withdraw consent at any time.</li>
						<li>Lodge a complaint with a supervisory authority.</li>
					</ul>
					<h3>Under CCPA (California):</h3>
					<ul>
						<li>
							Right to know what personal information we collect and how we use
							it.
						</li>
						<li>Right to request deletion of personal information.</li>
						<li>
							Right to opt out of sale of personal information (we do not sell
							data).
						</li>
						<li>Right to non-discrimination for exercising your rights.</li>
					</ul>
					<p>
						To exercise your rights, contact us at{' '}
						<a href="mailto:privacy@syrcha.com">privacy@syrcha.com</a>.
					</p>
				</>
			),
		},
		{
			id: 'cookies',
			title: 'Cookies & Tracking Technologies',
			body: (
				<>
					<p>We set cookies and similar storage for:</p>
					<ul>
						<li>
							<strong>Authentication and session management</strong>: strictly
							necessary, always set.
						</li>
						<li>
							<strong>Your preferences</strong>: such as your cookie choice
							itself, so we stop asking.
						</li>
						<li>
							<strong>Product analytics</strong>: PostHog, on EU infrastructure,
							loaded only after you accept. It records which pages and features
							are used. It does not follow you to other sites and we run no
							advertising or cross-site tracking.
						</li>
					</ul>
					<p>
						Declining leaves the product fully functional: only the analytics
						group is optional, and declining is as easy as accepting. You can
						change your answer at any time from the cookie banner or your
						browser settings.
					</p>
				</>
			),
		},
		{
			id: 'transfers',
			title: 'International Transfers',
			body: (
				<>
					<p>
						Your service data is stored in the EU. Syrcha operates from
						Australia, so our own staff access that data from outside the EEA, a
						transfer under Chapter V of the GDPR, covered by Standard
						Contractual Clauses.
					</p>
					<p>
						Sub-processors outside the EEA are engaged under the same
						safeguards. We say this plainly because "EU hosting" on its own does
						not mean data never leaves the EU, and a scanner that checks other
						people's transfer disclosures should not fudge its own.
					</p>
				</>
			),
		},
		{
			id: 'children',
			title: "Children's Privacy",
			body: (
				<p>
					Our Services are not directed to children under the age of 16 (or 13,
					where applicable). We do not knowingly collect data from children.
				</p>
			),
		},
		{
			id: 'changes',
			title: 'Changes to this Policy',
			body: (
				<p>
					We may update this Privacy Policy periodically. Updates will be posted
					on this page with the "Last Updated" date. We will notify users of
					significant changes by email or in-app notification.
				</p>
			),
		},
		{
			id: 'contact',
			title: 'Contact Us',
			body: (
				<>
					<p>
						If you have questions about this Privacy Policy or our practices,
						please contact us at:
					</p>
					<blockquote>
						<p>
							<strong>Syrcha Privacy Team</strong>
						</p>
						<p>
							Email: <a href="mailto:privacy@syrcha.com">privacy@syrcha.com</a>
						</p>
					</blockquote>
				</>
			),
		},
	]

	return (
		<LegalLayout
			eyebrow="/ legal · privacy"
			title={
				<>
					Privacy <span className="accent-underline">Policy</span>.
				</>
			}
			effectiveDate="2025-09-01"
			lastUpdatedDate="2026-08-07"
			intro={
				<>
					<p>
						This policy says what Syrcha collects, why, who else touches it, and
						how to get it back or get rid of it. It covers our website, the
						scanner, the API, and the extensions.
					</p>
					<p>
						We sell a product that checks whether other companies' privacy
						disclosures match what their sites actually do. So the standard here
						is not that this document sounds reassuring; it is that every named
						processor below is one we genuinely use, and every one we use is
						named. Where the answer is unflattering, it is still the answer.
					</p>
				</>
			}
			sections={sections}
		/>
	)
}
